import React from "react";
import { ArrowUpRight, BookOpen, Headphones } from "lucide-react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import useDailyTheme from "@/hooks/useDailyTheme";
import { track } from "@/lib/analytics";

/**
 * PalavraDoDia, protagonista único da nova Home devocional-first.
 *
 * Fonte: /api/today (via useDailyTheme).
 * Estrutura pastoral em 3 blocos: abertura, verdade, hoje (pastoral_blocks).
 * Regras:
 *  - Eyebrow com cycle_day: "Dia X de 365"
 *  - Título grande (Cormorant Garamond, azul-noite)
 *  - Versículo central em itálico
 *  - Blocos pastorais renderizados na íntegra
 *  - CTA único: "Começar agora" → /devocional
 *  - NUNCA usar em-dashes ou en-dashes (dash forbidden pela regra editorial)
 */
export default function PalavraDoDia() {
  const { theme } = useDailyTheme();
  const r = useReveal();

  if (!theme || theme.error) {
    return (
      <section
        data-testid="palavra-do-dia-loading"
        className="section-pad bg-[#F7F5F0]"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-40 bg-[#0B1E36]/10" />
            <div className="h-14 w-3/4 bg-[#0B1E36]/10" />
            <div className="h-4 w-full bg-[#0B1E36]/10 mt-8" />
            <div className="h-4 w-5/6 bg-[#0B1E36]/10" />
            <div className="h-4 w-4/6 bg-[#0B1E36]/10" />
          </div>
        </div>
      </section>
    );
  }

  const dev = theme.devotional || {};
  const cv = theme.central_verse || {};
  const pb = theme.pastoral_blocks || {};
  const cycleDay = theme.cycle_day;

  // Renderiza **palavra** como <strong> destacado
  const renderMarkup = (text) => {
    if (!text) return null;
    return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
      p.startsWith("**") && p.endsWith("**") ? (
        <strong key={i} className="text-[#C5A059] font-medium">
          {p.slice(2, -2)}
        </strong>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      )
    );
  };

  const renderParagraphs = (text) => {
    if (!text) return null;
    return text.split(/\n\n+/).map((para, i) => (
      <p
        key={i}
        className="text-[#1F1A17]/85 leading-[1.85] text-[1.05rem] md:text-[1.12rem] font-light text-justify"
        style={{ hyphens: "none", WebkitHyphens: "none", msHyphens: "none" }}
      >
        {renderMarkup(para)}
      </p>
    ));
  };

  return (
    <section
      id="palavra-do-dia"
      data-testid="palavra-do-dia-section"
      style={{
        paddingTop:
          "calc(var(--promo-bar-height, 0px) + var(--weekly-banner-height, 0px) + 6.5rem)",
      }}
      className="relative bg-[#F7F5F0] pb-20 md:pb-28 overflow-hidden"
    >
      {/* Ornamento sutil dourado no topo */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-[#C5A059]" aria-hidden />

      <div className="max-w-4xl mx-auto px-6 md:px-8">
        <div ref={r} className="reveal">
          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="h-px w-8 bg-[#C5A059]/60" aria-hidden />
            <span
              data-testid="palavra-do-dia-eyebrow"
              className="text-[0.7rem] uppercase tracking-[0.32em] text-[#0B1E36]/75 font-medium"
            >
              Palavra do Dia
              {cycleDay ? (
                <span className="ml-3 text-[#C5A059]">
                  Dia {cycleDay} de 365
                </span>
              ) : null}
            </span>
            <span className="h-px w-8 bg-[#C5A059]/60" aria-hidden />
          </div>

          {/* Linha de contexto humano, sutil e discreta */}
          <p
            data-testid="palavra-do-dia-context"
            className="text-center text-[0.82rem] md:text-[0.88rem] text-[#0B1E36]/60 font-light tracking-[0.005em] mb-6 md:mb-8"
          >
            Um espaço diário de fé, criado por{" "}
            <Link
              to="/sobre"
              className="text-[#0B1E36]/80 underline decoration-[#C5A059]/40 underline-offset-4 hover:text-[#C5A059] transition-colors"
              data-testid="palavra-do-dia-context-link"
            >
              Carlos e Milena Medeiros
            </Link>
            .
          </p>

          {/* Título do devocional */}
          <h1
            data-testid="palavra-do-dia-title"
            className="text-center font-serif-display text-[#0B1E36] text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.03] tracking-[-0.028em] font-medium"
          >
            {dev.title || "Devocional de hoje"}
          </h1>

          {/* Card devocional central */}
          <article
            data-testid="palavra-do-dia-card"
            className="mt-14 md:mt-16 bg-white border border-[#0B1E36]/8 p-8 md:p-14 lg:p-16 relative"
          >
            <div className="absolute top-0 left-0 w-16 h-px bg-[#C5A059]" aria-hidden />

            {/* Versículo central */}
            {cv.text && (
              <div className="text-center max-w-2xl mx-auto">
                <p
                  data-testid="palavra-do-dia-verse-text"
                  className="font-serif-display italic text-[1.35rem] md:text-[1.65rem] text-[#0B1E36]/90 leading-[1.55] tracking-[-0.008em]"
                >
                  &ldquo;{cv.text}&rdquo;
                </p>
                <p
                  data-testid="palavra-do-dia-verse-ref"
                  className="mt-5 text-[0.72rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium"
                >
                  {cv.reference}
                  {dev.read_time_min ? (
                    <span className="ml-3 text-[#0B1E36]/40">
                      {dev.read_time_min} min de leitura
                    </span>
                  ) : null}
                </p>
              </div>
            )}

            {/* Separador */}
            <div className="my-10 md:my-12 flex items-center gap-4">
              <div className="flex-1 h-px bg-[#0B1E36]/8" />
              <span className="text-[0.6rem] uppercase tracking-[0.32em] text-[#C5A059]/70">
                Reflexão
              </span>
              <div className="flex-1 h-px bg-[#0B1E36]/8" />
            </div>

            {/* Abertura */}
            {pb.abertura && (
              <div
                data-testid="palavra-do-dia-abertura"
                className="space-y-5 max-w-2xl mx-auto"
              >
                {renderParagraphs(pb.abertura)}
              </div>
            )}

            {/* Verdade Central */}
            {pb.verdade && (
              <div className="mt-12 max-w-2xl mx-auto">
                <p className="text-[0.62rem] uppercase tracking-[0.32em] text-[#0B1E36]/50 mb-4 font-medium">
                  Verdade Central
                </p>
                <blockquote
                  data-testid="palavra-do-dia-verdade"
                  className="font-serif-display italic text-[1.2rem] md:text-[1.35rem] text-[#0B1E36] leading-[1.65] tracking-[-0.008em] border-l-2 border-[#C5A059] pl-6"
                >
                  {renderMarkup(pb.verdade)}
                </blockquote>
              </div>
            )}

            {/* Aplicação Hoje */}
            {pb.hoje && (
              <div className="mt-12 max-w-2xl mx-auto">
                <p className="text-[0.62rem] uppercase tracking-[0.32em] text-[#0B1E36]/50 mb-4 font-medium">
                  Para Hoje
                </p>
                <div
                  data-testid="palavra-do-dia-hoje"
                  className="space-y-4 text-[#1F1A17]/85 leading-[1.85] text-[1.05rem] md:text-[1.12rem] font-light text-justify"
                  style={{ hyphens: "none", WebkitHyphens: "none", msHyphens: "none" }}
                >
                  {pb.hoje.split(/\n\n+/).map((p, i) => (
                    <p key={i}>{renderMarkup(p)}</p>
                  ))}
                </div>
              </div>
            )}

            {/* CTA único, pastoral e convidativo */}
            <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-5 max-w-2xl mx-auto">
              <a
                href="/devocional"
                data-testid="palavra-do-dia-cta"
                onClick={() =>
                  track("devotional_open", { source: "palavra_do_dia_home" })
                }
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#0B1E36] hover:bg-[#0B2545] text-white px-8 py-4 text-[0.78rem] uppercase tracking-[0.28em] font-medium transition-colors"
              >
                <BookOpen size={16} strokeWidth={1.5} />
                Começar agora
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </a>
              <button
                type="button"
                disabled
                aria-disabled="true"
                data-testid="palavra-do-dia-listen"
                title="Versão em áudio em breve"
                className="text-[0.68rem] uppercase tracking-[0.28em] text-[#0B1E36]/45 inline-flex items-center gap-2 border-b border-dashed border-[#0B1E36]/15 pb-1.5 cursor-not-allowed"
              >
                <Headphones size={13} strokeWidth={1.6} />
                Ouvir devocional
                <span className="text-[0.55rem] tracking-[0.3em] text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/25 px-2 py-0.5 ml-1">
                  Em breve
                </span>
              </button>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
